import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { object, func, string } from 'prop-types';
import { useJumplink } from 'yoda-core-components/lib/helpers/Stylitics';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { StyliticWidget } from 'yoda-site-components/lib/components/Stylitics';
import STYLITICS_STYLES from './styliticsStyles';

/**
 * PDP-specific wrapper around StyliticWidget.
 * Handles: PDP title styling, jumplink visibility, CSS injection.
 */
const StyliticWidgetPDP = ({
    config,
    productDetails = {},
    selectedColor = '',
    preferences = {},
    featureFlags = {},
    onJumpImage = null,
    onOutfitsData = null,
}) => {
    const dispatch = useDispatch();
    const [outfits, setOutfits] = useState(null);
    const containerRef = useRef(null);
    const hasLoadedOnce = useRef(false);

    // Register onLookClick callback for outfit click analytics
    useEffect(() => {
        if (config.registerLookClick && config.analyticsLookClickAction) {
            config.registerLookClick((outfit) => {
                if (outfit) {
                    dispatch(config.analyticsLookClickAction({ id: outfit.id, position: 0 }));
                }
            });
        }
    }, [config, dispatch]);

    // Reset outfits when color changes so stale data doesn't persist
    /* istanbul ignore next */
    useEffect(() => {
        if (hasLoadedOnce.current) {
            setOutfits([]);
        }
    }, [selectedColor]);

    // Inject global CSS on mount
    useEffect(() => {
        const existingStyle = document.querySelector('[data-stylitics-pdp]');
        if (existingStyle) return;

        const style = document.createElement('style');
        style.setAttribute('data-stylitics-pdp', 'true');
        style.textContent = STYLITICS_STYLES;
        document.head.appendChild(style);

        return () => {
            if (style.parentNode) style.parentNode.removeChild(style);
        };
    }, []);

    // Bind containerRef to DOM element
    useEffect(() => {
        const el = document.getElementById(config.containerId);
        if (el) containerRef.current = el;
    });

    // useJumplink from core-components
    const jumpImage = useJumplink(containerRef, outfits, config.imageQuery);

    // Pass jumplink image URL up to Common.jsx
    /* istanbul ignore next */
    useEffect(() => {
        if (jumpImage) {
            onJumpImage?.(jumpImage);
        } else if (hasLoadedOnce.current) {
            onJumpImage?.(null);
        }
    }, [jumpImage, onJumpImage]);

    // Toggle jumplink visibility when outfits load/unload
    /* istanbul ignore next */
    useEffect(() => {
        const jumplinkEl = document.getElementById('stylitics-jumplink');
        const hasOutfits =
            outfits &&
            (Array.isArray(outfits) ? outfits.length > 0 : Object.keys(outfits).length > 0);

        if (jumplinkEl) {
            if (hasOutfits) {
                jumplinkEl.style.display = 'block';
            } else if (outfits !== null) {
                jumplinkEl.style.display = 'none';
            }
        }
    }, [outfits]);

    /* istanbul ignore next */
    const handleOutfitsLoaded = useCallback(
        (data) => {
            hasLoadedOnce.current = true;
            setOutfits(data);
            onOutfitsData?.(data);
        },
        [onOutfitsData]
    );

    const hasOutfits =
        outfits && (Array.isArray(outfits) ? outfits.length > 0 : Object.keys(outfits).length > 0);

    return (
        <div>
            {hasOutfits && config.title && (
                <h4
                    id={config.titleId || 'widget-title'}
                    className={dt([
                        'text-center',
                        'font-open-sans-semibold',
                        '!leading-8',
                        'lg:text-xLg',
                        'sm:text-lg',
                        'pb-3',
                    ])}
                >
                    {config.title}
                </h4>
            )}
            <StyliticWidget
                config={config}
                productDetails={productDetails}
                selectedColor={selectedColor}
                preferences={preferences}
                featureFlags={featureFlags}
                onOutfitsLoaded={handleOutfitsLoaded}
                showTitle={false}
                showLoader={false}
            />
        </div>
    );
};

StyliticWidgetPDP.propTypes = {
    config: object.isRequired,
    productDetails: object,
    selectedColor: string,
    preferences: object,
    featureFlags: object,
    onJumpImage: func,
    onOutfitsData: func,
};

export default StyliticWidgetPDP;
